import { useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { Mail, Phone, Send, Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters").max(100),
  email: z.string().email("Please enter a valid email address"),
  telephone: z.string().min(5, "Please enter a valid phone number").max(20),
  message: z.string().min(10, "Message must be at least 10 characters").max(2000),
});

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, data);
      setIsSubmitted(true);
      toast.success("Message sent successfully! We'll be in touch soon.");
      reset();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-24 md:pt-32 bg-black min-h-screen">
      <Helmet>
        <title>Contact Us | Get a Free Consultation | Barton</title>
        <meta name="description" content="Ready to grow your business online? Contact Barton for a free consultation. Call 01704 777881, email hello@barton.uk, or fill out our enquiry form." />
        <meta property="og:title" content="Contact Barton | Get a Free Consultation" />
        <meta property="og:description" content="Ready to grow your business online? Contact us for a free consultation. We're here to help your small business thrive." />
        <meta property="og:url" content="https://barton.uk/contact" />
        <meta property="og:image" content="https://barton.uk/og-image.png" />
        <meta name="twitter:title" content="Contact Barton | Get a Free Consultation" />
        <meta name="twitter:description" content="Ready to grow your business online? Contact us for a free consultation." />
        <meta name="twitter:image" content="https://barton.uk/og-image.png" />
      </Helmet>
      {/* Hero Section */}
      <section className="container-custom py-12 md:py-20">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500/10 text-pink-400 text-sm font-medium mb-6">
            Get In Touch
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
            Let's start a{" "}
            <span className="text-gradient">conversation</span>
          </h1>
          <p className="text-white/60 text-lg leading-relaxed">
            Ready to take your business to the next level? We'd love to hear from you. 
            Fill out the form below and we'll get back to you within 24 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-[#111111] rounded-2xl border border-white/10 shadow-lg p-6 md:p-8">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-pink-400" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold mb-4 text-white">Message Sent!</h3>
                  <p className="text-white/60 mb-6">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    data-testid="send-another-button"
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white rounded-full px-8"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Name Field */}
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-medium text-white">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        data-testid="contact-name-input"
                        placeholder="John Smith"
                        className={`rounded-xl h-12 bg-black border-white/20 text-white placeholder:text-white/40 focus:border-purple-500 ${errors.name ? "border-red-500" : ""}`}
                        {...register("name")}
                      />
                      {errors.name && (
                        <p className="text-red-400 text-xs">{errors.name.message}</p>
                      )}
                    </div>

                    {/* Email Field */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-white">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-email-input"
                        placeholder="john@example.com"
                        className={`rounded-xl h-12 bg-black border-white/20 text-white placeholder:text-white/40 focus:border-purple-500 ${errors.email ? "border-red-500" : ""}`}
                        {...register("email")}
                      />
                      {errors.email && (
                        <p className="text-red-400 text-xs">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Telephone Field */}
                  <div className="space-y-2">
                    <Label htmlFor="telephone" className="text-sm font-medium text-white">
                      Phone Number *
                    </Label>
                    <Input
                      id="telephone"
                      type="tel"
                      data-testid="contact-telephone-input"
                      placeholder="+44 123 456 7890"
                      className={`rounded-xl h-12 bg-black border-white/20 text-white placeholder:text-white/40 focus:border-purple-500 ${errors.telephone ? "border-red-500" : ""}`}
                      {...register("telephone")}
                    />
                    {errors.telephone && (
                      <p className="text-red-400 text-xs">{errors.telephone.message}</p>
                    )}
                  </div>

                  {/* Message Field */}
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-sm font-medium text-white">
                      Your Message *
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-message-input"
                      placeholder="Tell us about your project..."
                      rows={6}
                      className={`rounded-xl resize-none bg-black border-white/20 text-white placeholder:text-white/40 focus:border-purple-500 ${errors.message ? "border-red-500" : ""}`}
                      {...register("message")}
                    />
                    {errors.message && (
                      <p className="text-red-400 text-xs">{errors.message.message}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white rounded-full h-14 text-lg"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Info Cards */}
            <div className="bg-[#111111] rounded-2xl p-6 border border-white/10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-pink-400" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold mb-1 text-white">Email Us</h3>
                  <a 
                    href="mailto:hello@barton.uk" 
                    data-testid="contact-email-link"
                    className="text-white/60 hover:text-pink-400 transition-colors"
                  >
                    hello@barton.uk
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-[#111111] rounded-2xl p-6 border border-white/10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold mb-1 text-white">Call Us</h3>
                  <a 
                    href="tel:01704777881" 
                    data-testid="contact-phone-link"
                    className="text-white/60 hover:text-purple-400 transition-colors block"
                  >
                    01704 777881
                  </a>
                  <a 
                    href="https://wa.me/441704777881"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-link"
                    className="text-[#25D366] hover:text-[#25D366]/80 transition-colors text-sm flex items-center gap-1 mt-1"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Message on WhatsApp
                  </a>
                </div>
              </div>
            </div>

            {/* Response Time */}
            <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl p-6 border border-white/10">
              <h3 className="font-heading font-semibold mb-2 text-white">Quick Response</h3>
              <p className="text-white/60 text-sm mb-4">
                We typically respond to all inquiries within 24 hours during business days.
              </p>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse" />
                <span className="text-sm text-white/70">Available for new projects</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
